/**
 * Pure-function resolver for the notification thumbnail icon.
 *
 * Threat model:
 *   The Service Worker MUST NEVER perform a network fetch for a notification
 *   thumbnail. A hostile server could redirect a thumbnail URL to leak the
 *   requester's IP, leak which message triggered the notification (timing
 *   side channel), or steal session cookies via a third-party origin. The
 *   only legal source for a notification thumbnail is the local,
 *   encrypted-at-rest thumbnail store (`AttachmentThumbnailStore`).
 *
 *   When the local cache misses, the notification renders WITHOUT an icon.
 *   We DO NOT fall back to a remote URL. We DO NOT schedule a background
 *   fetch. The notification is delivered icon-less and the user sees the app
 *   default — a small UX cost in exchange for a hard guarantee that the SW
 *   never reaches out to a peer-controlled origin during push handling.
 *
 * Closed-discriminated-union return shape:
 *
 *   `{ kind: 'fromCache', bytes: Uint8Array }`
 *     A cached thumbnail was found and decrypted. Caller passes `bytes` to
 *     the notification icon (typically via a `data:` URL).
 *
 *   `{ kind: 'missing' }`
 *     No cached thumbnail for `(attachmentId, bucket)`. Caller MUST emit
 *     the notification with no `icon` field set (or the app default).
 *
 * There is no third return shape. There is no `{ kind: 'fetchedRemote' }`,
 * no `{ kind: 'pendingFetch' }`, no `{ kind: 'remoteUrl', url }`. The closed
 * union is the invariant; adding a remote-fetch variant would be a
 * threat-model regression and is caught at the type level by exhaustiveness
 * checking in the SW push handler.
 *
 * Pure logic: the resolver is a thin wrapper around a caller-supplied cache
 * read function. The wrapper enforces the "no remote fetch" rule by ONLY
 * exposing the cache read; it has no `fetch` parameter, no URL parameter,
 * no network primitive in its signature.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs.
 *   Allowed: IndexedDB (via the injected cache callback), standard JS globals.
 *
 * Mirrors the iOS NSE polish counterpart `NSEThumbnailResolver.swift`.
 */

import type { ThumbnailDimensionBucket } from "../attachment-thumbnails/AttachmentThumbnail";

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

/**
 * Stable reference to a thumbnail in the local encrypted cache. The caller
 * builds this from the attachment metadata that was decrypted alongside the
 * message — never from a peer- or server-supplied URL.
 *
 * `attachmentKeyBytes` is the same 32-byte root attachment key used to seal
 * the original ciphertext; the cache derives its own per-bucket key via
 * HKDF.
 */
export interface NotificationThumbnailRef {
  readonly attachmentId: string;
  readonly bucket: ThumbnailDimensionBucket;
  readonly attachmentKeyBytes: Uint8Array;
}

/**
 * Minimal cache surface the resolver consumes. Mirrors the relevant subset
 * of `AttachmentThumbnailStore` so a fake can be passed in self-tests
 * without spinning up IndexedDB.
 *
 * The interface deliberately exposes ONLY a get-by-(id, bucket, key) method.
 * There is no `getByUrl`, no `download`, no `prefetch`. The resolver cannot
 * reach the network through this contract.
 */
export interface NotificationThumbnailCache {
  readonly get: (
    attachmentId: string,
    bucket: ThumbnailDimensionBucket,
    attachmentKeyBytes: Uint8Array,
  ) => Promise<Uint8Array | null>;
}

/**
 * Closed-discriminated-union return shape. See file header for the threat
 * model rationale.
 */
export type NotificationThumbnailResolution =
  | { readonly kind: "fromCache"; readonly bytes: Uint8Array }
  | { readonly kind: "missing" };

const MISSING: NotificationThumbnailResolution = { kind: "missing" };

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolve a thumbnail strictly from the local encrypted cache.
 *
 * Behaviour:
 *   - Cache hit → `{ kind: 'fromCache', bytes }`.
 *   - Cache miss (no record OR record present but null bytes) → `{ kind: 'missing' }`.
 *   - Cache throws (corrupted ciphertext, IDB transaction failure, decrypt
 *     failure) → `{ kind: 'missing' }`. We swallow the error rather than
 *     propagate it because (a) the SW push handler MUST still render a
 *     notification (decrypt-failure UX is the generic fallback in
 *     `SWRenderPolicy`, not a throw), and (b) the threat model requires
 *     icon-less render on any cache problem — there is no second-chance
 *     remote fetch.
 *
 * @param ref   Stable reference to the desired thumbnail.
 * @param cache The local encrypted-at-rest thumbnail store.
 */
export async function resolveNotificationThumbnail(
  ref: NotificationThumbnailRef,
  cache: NotificationThumbnailCache,
): Promise<NotificationThumbnailResolution> {
  let bytes: Uint8Array | null;
  try {
    bytes = await cache.get(ref.attachmentId, ref.bucket, ref.attachmentKeyBytes);
  } catch {
    // Any cache failure collapses to "missing." See file header for why we
    // never fall back to a remote fetch.
    return MISSING;
  }

  if (bytes === null || bytes.byteLength === 0) {
    return MISSING;
  }

  return { kind: "fromCache", bytes };
}
